/**
 * Favorites store the hearted listing id (not the author's user id) so a
 * listener heart opens the Listening profile and a talker heart opens that
 * talker listing — never the other role's /u or /l page.
 */

import { listingCardDisplayName, talkerBookingDisplayName } from './listingCardCopy';
import { createSlug } from './urlHelpers';

export const FAVORITE_LISTING_IDS_KEY = 'favoriteListingIds';
export const FAVORITE_PROFILE_IDS_KEY = 'favoriteProfileIds';
export const FAVORITE_LIMIT = 40;

/**
 * UUID string from a listing, user, or raw id. Never walks author.
 *
 * @param {unknown} value
 * @returns {string|null}
 */
export const favoriteEntityId = value => {
  if (!value) {
    return null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value.uuid === 'string' && value.uuid.trim()) {
    return value.uuid;
  }
  if (typeof value.id === 'string' && value.id.trim()) {
    return value.id;
  }
  if (typeof value.id?.uuid === 'string' && value.id.uuid.trim()) {
    return value.id.uuid;
  }
  return null;
};

/**
 * @param {unknown} ids
 * @returns {string[]}
 */
export const normalizeFavoriteIds = ids => {
  const list = Array.isArray(ids) ? ids : [];
  return [
    ...new Set(list.map(favoriteEntityId).filter(id => typeof id === 'string' && id.trim())),
  ].slice(0, FAVORITE_LIMIT);
};

const privateFavoriteIds = (currentUser, key) =>
  normalizeFavoriteIds(currentUser?.attributes?.profile?.privateData?.[key]);

/**
 * @param {object|null} currentUser
 * @returns {string[]}
 */
export const favoriteListingIdsFromUser = currentUser =>
  privateFavoriteIds(currentUser, FAVORITE_LISTING_IDS_KEY);

/**
 * Talker-profile hearts. Separate from listing ids so a dual-role /u heart
 * never migrates onto that person's Listening listing.
 *
 * @param {object|null} currentUser
 * @returns {string[]}
 */
export const favoriteProfileIdsFromUser = currentUser =>
  privateFavoriteIds(currentUser, FAVORITE_PROFILE_IDS_KEY);

/**
 * @param {object|null} currentUser
 * @param {string} listingId
 * @returns {boolean}
 */
export const isFavoriteListing = (currentUser, listingId) =>
  !!listingId && favoriteListingIdsFromUser(currentUser).includes(listingId);

/**
 * @param {object|null} currentUser
 * @param {string} userId
 * @returns {boolean}
 */
export const isFavoriteProfile = (currentUser, userId) =>
  !!userId && favoriteProfileIdsFromUser(currentUser).includes(userId);

/**
 * @param {string[]} ids
 * @param {string} listingId
 * @returns {string[]}
 */
export const toggleFavoriteIds = (ids, listingId) => {
  const current = normalizeFavoriteIds(ids);
  const id = favoriteEntityId(listingId);
  if (!id) {
    return current;
  }
  if (current.includes(id)) {
    return current.filter(item => item !== id);
  }
  return [id, ...current].slice(0, FAVORITE_LIMIT);
};

/**
 * True when this entity is the hearted listing (Listening profile).
 *
 * @param {object|null} entity
 * @returns {boolean}
 */
export const isFavoriteListingEntity = entity => {
  if (!entity || typeof entity !== 'object') {
    return false;
  }
  if (entity.type === 'listing' || entity.type === 'ownListing') {
    return true;
  }
  if (entity.type === 'user' || entity.type === 'currentUser') {
    return false;
  }
  const publicData = entity.attributes?.publicData;
  if (publicData?.listingType || publicData?.transactionProcessAlias) {
    return true;
  }
  const title = entity.attributes?.title;
  return typeof title === 'string' && !entity.attributes?.profile;
};

/**
 * True when this entity is a hearted talker (/u) profile.
 *
 * @param {object|null} entity
 * @returns {boolean}
 */
export const isFavoriteProfileEntity = entity => {
  if (!entity || typeof entity !== 'object') {
    return false;
  }
  if (isFavoriteListingEntity(entity)) {
    return false;
  }
  return entity.type === 'user' || entity.type === 'currentUser' || !!entity.attributes?.profile;
};

/**
 * Label on a Favorites row: listing Display name, or talker profile name.
 *
 * @param {object|null} entity
 * @returns {string}
 */
export const favoriteDisplayName = entity => {
  if (!entity) {
    return '';
  }
  if (isFavoriteListingEntity(entity)) {
    const title = entity.attributes?.title;
    return typeof title === 'string' ? title.trim() : '';
  }
  const displayName = entity.attributes?.profile?.displayName;
  return typeof displayName === 'string' ? displayName.trim() : '';
};

/**
 * Listener screen name on a Favorites row and Choose button.
 * Never the listing title when the author profile name is present.
 *
 * @param {object|null} entity
 * @returns {string}
 */
export const favoriteRowScreenName = entity => {
  if (!entity) {
    return '';
  }
  if (isFavoriteProfileEntity(entity)) {
    return favoriteDisplayName(entity);
  }
  return (
    talkerBookingDisplayName(entity, entity?.author) ||
    favoriteDisplayName(entity) ||
    listingCardDisplayName(entity) ||
    ''
  );
};

/**
 * Listing title as a smaller subtitle when it differs from the screen name.
 *
 * @param {object|null} entity
 * @returns {string}
 */
export const favoriteRowSubtitle = entity => {
  if (!entity || isFavoriteProfileEntity(entity)) {
    return '';
  }
  const title = listingCardDisplayName(entity) || favoriteDisplayName(entity) || '';
  const name = favoriteRowScreenName(entity);
  if (!title || title === name) {
    return '';
  }
  return title;
};

/**
 * Open the exact hearted listing or talker profile.
 * A listing never uses author.id (that /u page is the other role).
 *
 * @param {object|null} listing
 * @returns {{ name: string, params: { id: string, slug?: string } }|null}
 */
export const favoriteListingRoute = listing => {
  const id = favoriteEntityId(listing);
  if (!id) {
    return null;
  }
  const authorId = favoriteEntityId(listing?.author);
  if (isFavoriteListingEntity(listing)) {
    const title = listing?.attributes?.title || favoriteDisplayName(listing) || '';
    return {
      name: 'ListingPage',
      params: { id, slug: createSlug(title || 'listing') },
    };
  }
  if (isFavoriteProfileEntity(listing) && (!authorId || authorId === id)) {
    return { name: 'ProfilePage', params: { id } };
  }
  const title = listing?.attributes?.title || '';
  return {
    name: 'ListingPage',
    params: { id, slug: createSlug(title || 'listing') },
  };
};

/**
 * Map stored favorite ids onto loaded listings/users.
 * `favoriteListingIds` recover an author/user leftover onto that author's
 * Listening listing. `favoriteProfileIds` stay on /u.
 *
 * @param {object} params
 * @param {string[]} [params.listingIds]
 * @param {string[]} [params.profileIds]
 * @param {object[]} [params.listings]
 * @param {object[]} [params.users]
 * @returns {object[]}
 */
export const resolveFavoriteEntities = ({
  listingIds = [],
  profileIds = [],
  listings = [],
  users = [],
} = {}) => {
  const listingsById = new Map();
  const listingsByAuthor = new Map();
  (listings || []).forEach(listing => {
    const listingId = favoriteEntityId(listing);
    if (listingId) {
      listingsById.set(listingId, listing);
    }
    const authorId = favoriteEntityId(listing?.author);
    if (authorId && !listingsByAuthor.has(authorId)) {
      listingsByAuthor.set(authorId, listing);
    }
  });
  const usersById = new Map();
  (users || []).forEach(user => {
    const userId = favoriteEntityId(user);
    if (userId) {
      usersById.set(userId, user);
    }
  });

  const fromListingBucket = normalizeFavoriteIds(listingIds)
    .map(id => listingsById.get(id) || listingsByAuthor.get(id) || usersById.get(id) || null)
    .filter(Boolean);
  const fromProfileBucket = normalizeFavoriteIds(profileIds)
    .map(id => usersById.get(id))
    .filter(Boolean);

  const seen = new Set();
  return [...fromListingBucket, ...fromProfileBucket].filter(entity => {
    const key = `${entity.type || 'listing'}:${favoriteEntityId(entity)}`;
    if (!key || seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

/**
 * Rewrite leftover author/user ids in the listing bucket to listing ids,
 * and move talker-only leftovers into the profile bucket.
 *
 * @param {string[]} listingIds
 * @param {object[]} listings
 * @param {object[]} [users]
 * @returns {{ listingIds: string[], profileIds: string[] }}
 */
export const partitionRecoveredFavorites = (listingIds = [], listings = [], users = []) => {
  const listingsById = new Map();
  const listingsByAuthor = new Map();
  (listings || []).forEach(listing => {
    const listingId = favoriteEntityId(listing);
    if (listingId) {
      listingsById.set(listingId, listing);
    }
    const authorId = favoriteEntityId(listing?.author);
    if (authorId && !listingsByAuthor.has(authorId)) {
      listingsByAuthor.set(authorId, listing);
    }
  });
  const usersById = new Set((users || []).map(favoriteEntityId).filter(Boolean));

  const nextListingIds = [];
  const movedProfileIds = [];
  normalizeFavoriteIds(listingIds).forEach(id => {
    if (listingsById.has(id)) {
      nextListingIds.push(id);
      return;
    }
    const recovered = listingsByAuthor.get(id);
    const recoveredId = favoriteEntityId(recovered);
    if (recoveredId) {
      nextListingIds.push(recoveredId);
      return;
    }
    if (usersById.has(id)) {
      movedProfileIds.push(id);
    }
  });
  return {
    listingIds: normalizeFavoriteIds(nextListingIds),
    profileIds: normalizeFavoriteIds(movedProfileIds),
  };
};

/**
 * Keep a Favorites row when the stored id is the listing or (legacy) author.
 *
 * @param {object|null} entity
 * @param {string[]} listingIds
 * @param {string[]} [profileIds]
 * @returns {boolean}
 */
export const isStoredFavoriteEntity = (entity, listingIds = [], profileIds = []) => {
  const id = favoriteEntityId(entity);
  if (!id) {
    return false;
  }
  if (isFavoriteProfileEntity(entity)) {
    return profileIds.includes(id) || listingIds.includes(id);
  }
  const authorId = favoriteEntityId(entity?.author);
  return listingIds.includes(id) || (!!authorId && listingIds.includes(authorId));
};

const withPrivateFavoriteIds = (user, patch) => {
  if (!user?.attributes?.profile) {
    return user;
  }
  return {
    ...user,
    attributes: {
      ...user.attributes,
      profile: {
        ...user.attributes.profile,
        privateData: {
          ...(user.attributes.profile.privateData || {}),
          ...patch,
        },
      },
    },
  };
};

/**
 * Optimistic currentUser shape after a favorite toggle.
 *
 * @param {object} user
 * @param {string[]} ids
 * @returns {object}
 */
export const applyFavoriteIdsToUser = (user, ids) =>
  withPrivateFavoriteIds(user, { [FAVORITE_LISTING_IDS_KEY]: normalizeFavoriteIds(ids) });

/**
 * @param {object} user
 * @param {string[]} ids
 * @returns {object}
 */
export const applyFavoriteProfileIdsToUser = (user, ids) =>
  withPrivateFavoriteIds(user, { [FAVORITE_PROFILE_IDS_KEY]: normalizeFavoriteIds(ids) });

/**
 * Persist recovered listing/profile id partitions onto currentUser.
 *
 * @param {object} user
 * @param {{ listingIds?: string[], profileIds?: string[] }} next
 * @returns {object}
 */
export const applyFavoriteBucketsToUser = (user, next = {}) => {
  const listingIds =
    next.listingIds != null
      ? normalizeFavoriteIds(next.listingIds)
      : favoriteListingIdsFromUser(user);
  const profileIds =
    next.profileIds != null
      ? normalizeFavoriteIds([...(favoriteProfileIdsFromUser(user) || []), ...next.profileIds])
      : favoriteProfileIdsFromUser(user);
  return withPrivateFavoriteIds(user, {
    [FAVORITE_LISTING_IDS_KEY]: listingIds,
    [FAVORITE_PROFILE_IDS_KEY]: profileIds,
  });
};
